
import * as React from 'react';
import { Button, Card, Col, Container, DropdownButton, Form, Row, Tab, Table, Tabs } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getAllMedium, getAllSubjectcategory, getSubjectCategoryDecipline, getSubjectbyCourseAndDiscipline, getCourseCreditPreference } from "../../api/studentDetails"
import { useEffect, useState } from 'react';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';

function MajorSubject({ onSave, courseId }) {
    const [medium, setMedium] = useState([]);
    const [selectedMedium, setselectedMedium] = useState("");
    const [error, setError] = useState('');
    const [majorGroups, setMajorGroups] = useState([]);
    const [selectedDiscipline, setSelectedDiscipline] = useState(null);
    const [subjects, setSubjects] = useState([]);
    const [selectedSubjects, setSelectedSubjects] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [selectedDisciplines, setSelectedDisciplines] = useState([]);
    const [maxPreference, setMaxPreference] = useState(0);
    const [creditRange, setCreditRange] = useState({ min: 0, max: 0 });
    const [errors, setErrors] = useState({});
    const [totalCombinedCredits, setTotalCombinedCredits] = useState(0);

    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const mediumResponse = await getAllMedium(token);
                const data = mediumResponse.data;
                setMedium(data);
                const englishMedium = data.find((m) => m.mediumDescription.toLowerCase() === "english");
                if (englishMedium) {
                    setselectedMedium(englishMedium.id);
                }

                const subjectCategoryResponse = await getAllSubjectcategory(token);
                const subjectCategories = subjectCategoryResponse.data;
                const majorCategories = subjectCategories.filter(category => category.checkIfMajor === true);

                let disciplines = [];
                for (const category of majorCategories) {
                    try {
                        const disciplineResponse = await getSubjectCategoryDecipline(token, category.id);
                        const filteredDisciplines = disciplineResponse.data.filter(d => {
                            const courseMatch = d.course?.id !== undefined &&
                                Number(d.course.id) === Number(courseId);
                            const categoryMatch = d.subjectCategory?.id === category.id;
                            return courseMatch && categoryMatch;
                        });

                        if (filteredDisciplines.length === 0) {
                            continue;
                        }

                        let creditInfo = { minCredits: 0, maxCredits: 0 };
                        try {
                            const creditResponse = await getCourseCreditPreference(token, category.id, courseId);
                            if (creditResponse.data) {
                                creditInfo = {
                                    minCredits: creditResponse.data.minCredits || 0,
                                    maxCredits: creditResponse.data.maxCredits || 0,
                                    maxPreference: creditResponse.data.maxPreference,
                                };
                            }
                        } catch (creditError) {
                            console.error("Error fetching credits for category", category.id, ":", creditError);
                        }

                        const extractedDisciplines = filteredDisciplines.map(d => ({
                            id: d.discipline.id,
                            name: d.discipline.disciplineName,
                            subjectCategoryName: d.subjectCategory.subjectCategoryName,
                            subjectCategoryId: d.subjectCategory.id,
                            courseId: d.course.id,
                            maxPreference: creditInfo.maxPreference,
                            minCredits: creditInfo.minCredits,
                            maxCredits: creditInfo.maxCredits
                        }));

                        disciplines = [...disciplines, ...extractedDisciplines];
                    } catch (err) {
                        console.error("Error in category", category.id, ":", err);
                    }
                }

                setMajorGroups(disciplines);
            } catch (error) {
                console.error("Error fetching data:", error);
                setError('Error fetching data: ' + (error.response?.data?.message || error.message));
            }
        };

        if (courseId) {
            fetchInitialData();
        }
    }, [token, courseId]);

    useEffect(() => {
        const fetchSubjects = async () => {
            if (courseId && selectedDiscipline) {
                try {
                    const response = await getSubjectbyCourseAndDiscipline(token, courseId, selectedDiscipline.id);
                    const filteredSubjects = response.data.filter(subject =>
                        subject.subjectCategory.id === selectedDiscipline.subjectCategoryId
                    );

                    const preSelected = filteredSubjects.filter(subject => !subject.elective).map(subject => subject.id);
                    setSelectedSubjects(preSelected);
                    setSubjects(filteredSubjects);
                } catch (error) {
                    console.error("Error fetching subjects:", error.response || error.message);
                }
            }
        };
        fetchSubjects();
    }, [courseId, selectedDiscipline, token]);

    const handleCheckboxChange = (e) => {
        const subjectId = parseInt(e.target.value);
        const subject = subjects.find(sub => sub.id === subjectId);
        if (!subject) return;

        setSelectedSubjects((prevSelected) => {
            let newSelected;
            if (prevSelected.includes(subjectId)) {
                newSelected = prevSelected.filter((id) => id !== subjectId);
            } else {
                newSelected = [...prevSelected, subjectId];
            }
            return newSelected;
        });
    };

    const validateSubjects = () => {
        if (selectedSubjects.length === 0) {
            setErrors({ subjects: "Please select at least one subject." });
            return false;
        }

        const selectedCredits = selectedSubjects.reduce((sum, id) => {
            const subject = subjects.find(s => s.id === id);
            return sum + (subject?.subjectDefinition.credits || 0);
        }, 0);

        if (selectedCredits < creditRange.min || selectedCredits > creditRange.max) {
            const errorMsg = `Total credits must be between ${creditRange.min} and ${creditRange.max}. Currently selected: ${selectedCredits}`;
            setError(errorMsg);
            toast.error(errorMsg);
            return false;
        }

        const newTotal = totalCombinedCredits + selectedCredits;
        if (newTotal > creditRange.max) {
            const errorMsg = `Total combined credits cannot exceed ${creditRange.max}. Currently selected: ${newTotal}`;
            setError(errorMsg);
            toast.error(errorMsg);
            return false;
        }

        return true;
    };

    const handleAddSubjects = () => {
        setError("");
        setErrors({});

        if (!validateSubjects()) {
            return;
        }

        const selectedCredits = selectedSubjects.reduce((sum, id) => {
            const subject = subjects.find(s => s.id === id);
            return sum + (subject?.subjectDefinition.credits || 0);
        }, 0);

        const selectedSubjectNames = subjects
            .filter(subject => selectedSubjects.includes(subject.id))
            .map(subject => subject.subjectDefinition.subjectName);

        const selectedSubjectIds = subjects
            .filter(subject => selectedSubjects.includes(subject.id))
            .map(subject => subject.subjectDefinition.id);

        const newRecord = {
            preference: tableData.length + 1,
            major: selectedDiscipline?.name || "N/A",
            selectedMajorGroupId: selectedDiscipline?.id || null,
            selectedSubjects: selectedSubjectIds,
            subjects: selectedSubjectNames.join(", "),
            subjectCategories: selectedDiscipline?.subjectCategoryId || null,
            credits: selectedCredits
        };

        setTableData([...tableData, newRecord]);
        setTotalCombinedCredits(totalCombinedCredits + selectedCredits);
        setSelectedSubjects([]);
    };

    useEffect(() => {
        if (majorGroups.length > 0) {
            const min = Math.min(...majorGroups.map(d => d.minCredits));
            const max = Math.max(...majorGroups.map(d => d.maxCredits));
            setCreditRange({ min, max });
        }
    }, [majorGroups]);

    const handleDeleteRow = (index) => {
        const rowToDelete = tableData[index];
        setTableData(tableData.filter((_, i) => i !== index));
        setTotalCombinedCredits(totalCombinedCredits - rowToDelete.credits);
    };

    useEffect(() => {
        if (majorGroups.length > 0) {
            const maxPref = Math.max(...majorGroups.map(d => d.maxPreference), 0);
            setMaxPreference(maxPref);
        }
    }, [majorGroups]);

    const handleMajorSelection = (e) => {
        const selectedId = parseInt(e.target.value);
        const selected = majorGroups.find(d => d.id === selectedId);
        if (!selected) return;

        setSelectedDisciplines((prevDisciplines) => [...prevDisciplines, selected]);
        setSelectedDiscipline(selected);
        setCreditRange({ min: selected.minCredits, max: selected.maxCredits });
        setErrors((prev) => ({ ...prev, selectedDiscipline: "", subjects: "" }));
    };

    const handleSaveAndNext = () => {
        const newErrors = {};
        if (!selectedDiscipline) {
            newErrors.selectedDiscipline = "Please Select Major Group before proceeding.";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        onSave(tableData);
    };

    return (
        <>
            <hr style={{ marginTop: "0px", background: "#6e48aa" }}></hr>
            <Form>
                <Row>
                    <Col>
                        <Row className='mb-2'>
                            <Form.Group className="mb-2">
                                <Form.Label>Medium / Instruction Medium<span className="text-danger">*</span></Form.Label>
                                <Form.Select sm={7} type="text"
                                    name="medium"
                                    value={selectedMedium}
                                    onChange={(e) => setselectedMedium(e.target.value)}>
                                    <option> Select Medium</option>
                                    {medium.map((data) => (
                                        <option key={data.id} value={data.id}>
                                            {data.mediumDescription}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Row>
                        <Row className='mb-2'>
                            <Form.Group className="mb-2">
                                <Form.Label>Major Group<span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    name="majorgroup"
                                    value={selectedDiscipline?.id || ""}
                                    onChange={handleMajorSelection}
                                    disabled={majorGroups.length === 0}
                                >
                                    {majorGroups.length === 0 ? (
                                        <option value="" disabled>Loading disciplines...</option>
                                    ) : (
                                        <>
                                            <option value="">Select Major Group</option>
                                            {majorGroups.map((group) => (
                                                <option key={group.id} value={group.id}>
                                                    {group.name}
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </Form.Select>
                                {errors.selectedDiscipline && (
                                    <div style={{ color: "red", fontSize: "13px", marginTop: "3px" }}>
                                        {errors.selectedDiscipline}
                                    </div>
                                )}
                                {selectedDiscipline && (
                                    <Row className="mb-2 mt-2">
                                        <Form.Group className="mb-2">
                                            <Form.Label> <span style={{ fontSize: "14px" }}>
                                                Max. Group Preference: {selectedDiscipline.maxPreference}
                                            </span></Form.Label>
                                        </Form.Group>
                                    </Row>
                                )}
                            </Form.Group>
                            <ToastContainer />
                        </Row>
                    </Col>
                    <Col>
                        {selectedDiscipline && (
                            <Row>
                                <Form.Label> {selectedDiscipline ? selectedDiscipline.subjectCategoryName : ""}
                                    <br></br>
                                    <span style={{ fontSize: "14px", fontWeight: "bold", color: "#f00" }}>
                                        Credit Range: Min {selectedDiscipline.minCredits} - Max {selectedDiscipline.maxCredits}
                                    </span>
                                </Form.Label>
                                <DropdownButton
                                    title={`All Selected Subjects (${selectedSubjects.length})`}
                                    id="course-dropdown" className="course-dropdown">
                                    <div style={{ padding: '10px' }}>
                                        {subjects.map((subject) => (
                                            <Form.Check
                                                key={subject.id}
                                                type="checkbox"
                                                id={`subject-${subject.id}`}
                                                value={subject.id}
                                                label={`${subject.subjectDefinition.subjectName} (CR: ${subject.subjectDefinition.credits})`}
                                                checked={!subject.elective || selectedSubjects.includes(subject.id)}
                                                disabled={!subject.elective}
                                                onChange={handleCheckboxChange}
                                            />
                                        ))}
                                    </div>
                                </DropdownButton>
                                {errors.subjects && (
                                    <div style={{ color: "red", fontSize: "13px", marginTop: "3px" }}>
                                        {errors.subjects}
                                    </div>
                                )}
                                <Col className="mt-2 d-flex align-items-center justify-content-center">
                                    <Button variant="secondary" type="button" onClick={handleAddSubjects}>
                                        Add
                                    </Button>
                                </Col>
                            </Row>
                        )}
                    </Col>
                </Row>

                <Row>
                    <Col className="d-flex align-items-center justify-content-center">
                        <Button variant="primary" type="button" onClick={handleSaveAndNext}>
                            Save And Next
                        </Button>
                    </Col>
                </Row>
            </Form>
            <Row>
                <b>Subject Group</b>
                <Table responsive striped bordered hover>
                    <thead style={{
                        backgroundColor: "#6e48aa",
                        color: "#fff",
                        fontSize: "15px",
                        fontWeight: "600",
                    }}>
                        <tr>
                            <th style={{ width: "10%" }}>Preference </th>
                            <th style={{ width: "20%" }}>Major</th>
                            <th>Subject </th>
                            <th style={{ width: "10%", textAlign: "center" }}>Action </th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map((row, index) => (
                            <tr key={index}>
                                <td>{row.preference}</td>
                                <td>{row.major}</td>
                                <td>{row.subjects}</td>
                                <td style={{ textAlign: "center" }}>
                                    <DeleteSweepIcon style={{ color: "#f00" }}
                                        onClick={() => handleDeleteRow(index)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Row>
        </>
    );
};

export default MajorSubject;